import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSiteSettings } from "@/contexts/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Upload, X, Loader2, Save, Plus, Trash2, Star, ArrowUp, ArrowDown, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AdminPasswordDialog from "@/components/AdminPasswordDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULTS = {
  // Branding
  app_name: "CampSpot",
  logo_image: "",
  brand_primary: "#059669",

  // Hero
  hero_image: "",
  hero_title: "",
  hero_subtitle: "",
  search_button_text: "Explorer",

  // How it works
  how_title: "Comment ca marche",
  step1_title: "", step1_desc: "",
  step2_title: "", step2_desc: "",
  step3_title: "", step3_desc: "",

  // Testimonials
  testimonials_title: "Ce que nos campeurs disent",

  // Host CTA
  host_cta_title: "Louez votre terrain et gagnez de l'argent",
  host_cta_desc: "Transformez votre lopin de terre en source de revenus.",
  host_cta_button: "Publier mon terrain",
  host_cta_image: "",

  // Footer
  footer_text: "",
  contact_email: "",
  terms_url: "",
  privacy_url: "",

  // Auth pages
  login_tagline: "",
  register_tagline: "",

  // Subscription page
  sub_title: "",
  sub_subtitle: "",

  // Superstar
  superstar_title: "",
  superstar_desc: "",

  // Spaces list
  list_title: "Terrains disponibles",
  list_empty_msg: "Aucun terrain trouve",

  // SEO
  seo_title: "",
  seo_description: "",

  // Promo banner
  banner_enabled: false,
  banner_text: "",
  banner_link_url: "",
  banner_link_label: "",
  banner_bg_color: "#059669",

  // Pre-launch mode
  prelaunch_enabled: false,
  prelaunch_title: "",
  prelaunch_subtitle: "",
  prelaunch_image: "",
  prelaunch_button_text: "",

  // Legal pages (markdown supported: # ## ** lists -)
  cgu_content: "",
  privacy_content: "",
  cancellation_content: "",
  cancellation_summary: "",

  // Booking disclaimer (yellow box on booking card)
  booking_disclaimer_title: "",
  booking_disclaimer_intro: "",
  booking_disclaimer_bullets: "",
  booking_disclaimer_accept_label: "",

  // About page (/about)
  about_title: "",
  about_subtitle: "",
  about_content: "",
  about_image: "",
};

function ImageField({ label, value, onChange, uploading, setUploading, testId, ratio = "h-32" }) {
  const ref = useRef(null);
  const previewSrc = value?.startsWith("/api/") ? `${BACKEND_URL}${value}` : value;
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploadee");
    } catch { toast.error("Erreur upload"); }
    finally { setUploading(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">{label}</Label>
      {value ? (
        <div className="relative rounded-xl overflow-hidden border border-zinc-200 bg-zinc-50">
          <img src={previewSrc} alt={label} className={`w-full ${ratio} object-cover`} />
          <button type="button" onClick={() => onChange("")} className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow" data-testid={`${testId}-remove`}>
            <X className="h-4 w-4 text-zinc-700" />
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => ref.current?.click()} disabled={uploading} className={`w-full ${ratio} rounded-xl border-2 border-dashed border-zinc-300 hover:border-emerald-400 hover:bg-emerald-50/30 transition-colors flex flex-col items-center justify-center gap-1 text-zinc-500`} data-testid={`${testId}-btn`}>
          {uploading ? (<><Loader2 className="h-5 w-5 animate-spin" /><span className="text-xs">Upload...</span></>) : (<><Upload className="h-5 w-5" /><span className="text-xs">Cliquer pour ajouter</span></>)}
        </button>
      )}
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={upload} data-testid={`${testId}-input`} />
    </div>
  );
}

function TextField({ label, value, onChange, placeholder, testId, type = "text", autoComplete = "off" }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">{label}</Label>
      <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="rounded-xl" data-testid={testId} autoComplete={autoComplete} name={testId || label} />
    </div>
  );
}

function TextAreaField({ label, value, onChange, placeholder, testId, rows = 3 }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">{label}</Label>
      <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={rows} className="rounded-xl" data-testid={testId} autoComplete="off" name={testId || label} />
    </div>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { reload: reloadGlobalSettings } = useSiteSettings();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdError, setPwdError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(DEFAULTS);
  const [testimonials, setTestimonials] = useState([]);
  const [footerLinks, setFooterLinks] = useState([]);
  const [navOrder, setNavOrder] = useState(["explore", "create", "superstar", "about"]);

  useEffect(() => {
    if (user?.role !== "admin") { navigate("/"); return; }
    api.get("/settings").then(r => {
      const s = r.data || {};
      setForm({ ...DEFAULTS, ...Object.fromEntries(Object.keys(DEFAULTS).map(k => [k, s[k] ?? DEFAULTS[k]])) });
      setTestimonials(Array.isArray(s.testimonials) ? s.testimonials : []);
      setFooterLinks(Array.isArray(s.footer_links) ? s.footer_links : []);
      if (Array.isArray(s.nav_order) && s.nav_order.length) setNavOrder(s.nav_order);
    }).catch(() => toast.error("Erreur de chargement")).finally(() => setLoading(false));
  }, [user, navigate]);

  const setField = (k) => (v) => setForm(f => ({ ...f, [k]: v }));

  const addTestimonial = () => setTestimonials(t => [...t, { name: "", text: "", rating: 5 }]);
  const updTestimonial = (i, k, v) => setTestimonials(t => t.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const delTestimonial = (i) => setTestimonials(t => t.filter((_, idx) => idx !== i));

  const addFooterLink = () => setFooterLinks(l => [...l, { label: "", url: "" }]);
  const updFooterLink = (i, k, v) => setFooterLinks(l => l.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const delFooterLink = (i) => setFooterLinks(l => l.filter((_, idx) => idx !== i));

  const handleSave = () => { setPwdError(""); setPwdOpen(true); };

  const submitWithPassword = async (password) => {
    setSaving(true);
    setPwdError("");
    try {
      await api.put("/settings", { ...form, testimonials, footer_links: footerLinks, nav_order: navOrder, admin_password: password });
      toast.success("Parametres sauvegardes");
      setPwdOpen(false);
      reloadGlobalSettings();
    } catch (err) {
      setPwdError(err.response?.data?.detail || "Erreur de sauvegarde");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-emerald-700" /></div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6 pb-24">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-zinc-950" data-testid="settings-title">Parametres du site</h1>
        <p className="text-sm text-zinc-500 mt-1">Personnalisez chaque page de votre site. Toutes les modifications s'appliquent immediatement après la sauvegarde.</p>
      </div>

      <Accordion type="multiple" defaultValue={["branding", "hero"]} className="space-y-3">

        {/* === BRANDING === */}
        <AccordionItem value="branding" className="border border-zinc-200 rounded-2xl px-4 bg-white">
          <AccordionTrigger className="font-heading text-base font-semibold hover:no-underline" data-testid="section-branding">Marque & identité</AccordionTrigger>
          <AccordionContent className="space-y-4 pt-2">
            <TextField label="Nom du site" value={form.app_name} onChange={setField("app_name")} placeholder="CampSpot" testId="setting-app-name" />
            <ImageField label="Logo (PNG transparent recommande)" value={form.logo_image} onChange={setField("logo_image")} uploading={uploading} setUploading={setUploading} testId="setting-logo" />
            <TextField label="Couleur principale (HEX)" value={form.brand_primary} onChange={setField("brand_primary")} placeholder="#059669" testId="setting-brand-primary" />
          </AccordionContent>
        </AccordionItem>

        {/* === HERO === */}
        <AccordionItem value="hero" className="border border-zinc-200 rounded-2xl px-4 bg-white">
          <AccordionTrigger className="font-heading text-base font-semibold hover:no-underline" data-testid="section-hero">Hero d'accueil (haut de la page d'accueil)</AccordionTrigger>
          <AccordionContent className="space-y-4 pt-2">
            <ImageField label="Image de fond" value={form.hero_image} onChange={setField("hero_image")} uploading={uploading} setUploading={setUploading} testId="setting-hero-img" ratio="h-40" />
            <TextField label="Titre principal" value={form.hero_title} onChange={setField("hero_title")} placeholder="Trouvez votre coin de paradis" testId="setting-hero-title" />
            <TextField label="Sous-titre" value={form.hero_subtitle} onChange={setField("hero_subtitle")} placeholder="Reservez un terrain de camping privé..." testId="setting-hero-subtitle" />
            <TextField label="Libelle du bouton de recherche" value={form.search_button_text} onChange={setField("search_button_text")} placeholder="Explorer" testId="setting-search-btn" />
          </AccordionContent>
        </AccordionItem>

        {/* === HOW IT WORKS === */}
        <AccordionItem value="how" className="border border-zinc-200 rounded-2xl px-4 bg-white">
          <AccordionTrigger className="font-heading text-base font-semibold hover:no-underline" data-testid="section-how">Section "Comment ca marche"</AccordionTrigger>
          <AccordionContent className="space-y-4 pt-2">
            <TextField label="Titre de la section" value={form.how_title} onChange={setField("how_title")} placeholder="Comment ca marche" testId="setting-how-title" />
            {[1, 2, 3].map(n => (
              <div key={n} className="rounded-xl border border-zinc-200 p-3 space-y-2">
                <p className="text-xs font-semibold text-emerald-700">Etape {n}</p>
                <TextField label="Titre" value={form[`step${n}_title`]} onChange={setField(`step${n}_title`)} testId={`setting-step${n}-title`} />
                <TextAreaField label="Description" value={form[`step${n}_desc`]} onChange={setField(`step${n}_desc`)} testId={`setting-step${n}-desc`} rows={2} />
              </div>
            ))}
          </AccordionContent>
        </AccordionItem>

        {/* === TESTIMONIALS === */}
        <AccordionItem value="testimonials" className="border border-zinc-200 rounded-2xl px-4 bg-white">
          <AccordionTrigger className="font-heading text-base font-semibold hover:no-underline" data-testid="section-testimonials">Temoignages</AccordionTrigger>
          <AccordionContent className="space-y-4 pt-2">
            <TextField label="Titre de la section" value={form.testimonials_title} onChange={setField("testimonials_title")} testId="setting-testimonials-title" />
            <div className="space-y-3">
              {testimonials.map((t, i) => (
                <div key={i} className="rounded-xl border border-zinc-200 p-3 space-y-2 relative">
                  <button type="button" onClick={() => delTestimonial(i)} className="absolute top-2 right-2 text-zinc-400 hover:text-red-600" data-testid={`del-testimonial-${i}`}><Trash2 className="h-4 w-4" /></button>
                  <TextField label="Nom" value={t.name} onChange={(v) => updTestimonial(i, "name", v)} testId={`testimonial-name-${i}`} />
                  <TextAreaField label="Temoignage" value={t.text} onChange={(v) => updTestimonial(i, "text", v)} testId={`testimonial-text-${i}`} rows={2} />
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map(n => (
                      <button key={n} type="button" onClick={() => updTestimonial(i, "rating", n)} data-testid={`testimonial-${i}-star-${n}`}>
                        <Star className={`h-4 w-4 ${n <= (t.rating || 5) ? "fill-amber-400 text-amber-400" : "text-zinc-300"}`} />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={addTestimonial} className="rounded-xl" data-testid="add-testimonial-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Ajouter un temoignage</Button>
          </AccordionContent>
        </AccordionItem>

        {/* === HOST CTA === */}
        <AccordionItem value="cta" className="border border-zinc-200 rounded-2xl px-4 bg-white">
          <AccordionTrigger className="font-heading text-base font-semibold hover:no-underline" data-testid="section-cta">Section "Devenir hôte" (page d'accueil)</AccordionTrigger>
          <AccordionContent className="space-y-4 pt-2">
            <ImageField label="Image" value={form.host_cta_image} onChange={setField("host_cta_image")} uploading={uploading} setUploading={setUploading} testId="setting-cta-img" ratio="h-40" />
            <TextField label="Titre" value={form.host_cta_title} onChange={setField("host_cta_title")} testId="setting-cta-title" />
            <TextAreaField label="Description" value={form.host_cta_desc} onChange={setField("host_cta_desc")} testId="setting-cta-desc" />
            <TextField label="Libelle du bouton" value={form.host_cta_button} onChange={setField("host_cta_button")} testId="setting-cta-btn" />
          </AccordionContent>
        </AccordionItem>

        {/* === AUTH PAGES === */}
        <AccordionItem value="auth" className="border border-zinc-200 rounded-2xl px-4 bg-white">
          <AccordionTrigger className="font-heading text-base font-semibold hover:no-underline" data-testid="section-auth">Pages de connexion / inscription</AccordionTrigger>
          <AccordionContent className="space-y-4 pt-2">
            <TextField label="Accroche page de connexion" value={form.login_tagline} onChange={setField("login_tagline")} placeholder="Connectez-vous a votre compte" testId="setting-login-tagline" />
            <TextField label="Accroche page d'inscription" value={form.register_tagline} onChange={setField("register_tagline")} placeholder="Créez votre compte" testId="setting-register-tagline" />
          </AccordionContent>
        </AccordionItem>

        {/* === SUBSCRIPTION PAGE === */}
        <AccordionItem value="sub" className="border border-zinc-200 rounded-2xl px-4 bg-white">
          <AccordionTrigger className="font-heading text-base font-semibold hover:no-underline" data-testid="section-sub">Page Abonnement hôte</AccordionTrigger>
          <AccordionContent className="space-y-4 pt-2">
            <TextField label="Titre principal" value={form.sub_title} onChange={setField("sub_title")} placeholder="Publiez vos terrains et accueillez des campeurs" testId="setting-sub-title" />
            <TextAreaField label="Sous-titre" value={form.sub_subtitle} onChange={setField("sub_subtitle")} placeholder="Un seul abonnement annuel..." testId="setting-sub-subtitle" />
          </AccordionContent>
        </AccordionItem>

        {/* === SUPERSTAR === */}
        <AccordionItem value="superstar" className="border border-zinc-200 rounded-2xl px-4 bg-white">
          <AccordionTrigger className="font-heading text-base font-semibold hover:no-underline" data-testid="section-superstar">Page Superstar</AccordionTrigger>
          <AccordionContent className="space-y-4 pt-2">
            <TextField label="Titre principal" value={form.superstar_title} onChange={setField("superstar_title")} placeholder="Devenez Hôte Superstar" testId="setting-ss-title" />
            <TextAreaField label="Description" value={form.superstar_desc} onChange={setField("superstar_desc")} placeholder="Recompense les hôtes..." testId="setting-ss-desc" />
          </AccordionContent>
        </AccordionItem>

        {/* === SPACES LIST === */}
        <AccordionItem value="list" className="border border-zinc-200 rounded-2xl px-4 bg-white">
          <AccordionTrigger className="font-heading text-base font-semibold hover:no-underline" data-testid="section-list">Page liste des terrains</AccordionTrigger>
          <AccordionContent className="space-y-4 pt-2">
            <TextField label="Titre de la page" value={form.list_title} onChange={setField("list_title")} placeholder="Terrains disponibles" testId="setting-list-title" />
            <TextField label="Message si aucun resultat" value={form.list_empty_msg} onChange={setField("list_empty_msg")} placeholder="Aucun terrain trouve" testId="setting-list-empty" />
          </AccordionContent>
        </AccordionItem>

        {/* === FOOTER === */}
        <AccordionItem value="footer" className="border border-zinc-200 rounded-2xl px-4 bg-white">
          <AccordionTrigger className="font-heading text-base font-semibold hover:no-underline" data-testid="section-footer">Pied de page</AccordionTrigger>
          <AccordionContent className="space-y-4 pt-2">
            <TextAreaField label="Texte du pied de page" value={form.footer_text} onChange={setField("footer_text")} placeholder="© 2026 ParkBnBe. Tous droits réservés." testId="setting-footer-text" rows={2} />
            <TextField label="Email de contact" value={form.contact_email} onChange={setField("contact_email")} placeholder="contact@parkbnbe.com" type="email" testId="setting-contact-email" />
            <TextField label="URL Conditions d'utilisation (externe — facultatif)" value={form.terms_url} onChange={setField("terms_url")} placeholder="https://..." testId="setting-terms-url" />
            <TextField label="URL Politique de confidentialite (externe — facultatif)" value={form.privacy_url} onChange={setField("privacy_url")} placeholder="https://..." testId="setting-privacy-url" />

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Liens supplementaires</Label>
              {footerLinks.map((l, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <Input value={l.label} onChange={(e) => updFooterLink(i, "label", e.target.value)} placeholder="Libelle" className="rounded-xl flex-1" data-testid={`footer-link-label-${i}`} />
                  <Input value={l.url} onChange={(e) => updFooterLink(i, "url", e.target.value)} placeholder="https://..." className="rounded-xl flex-1" data-testid={`footer-link-url-${i}`} />
                  <Button type="button" variant="ghost" size="icon" onClick={() => delFooterLink(i)} className="text-zinc-400 hover:text-red-600 flex-shrink-0" data-testid={`del-footer-link-${i}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addFooterLink} className="rounded-xl" data-testid="add-footer-link-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Ajouter un lien</Button>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* === LEGAL PAGES === */}
        <AccordionItem value="legal" className="border border-zinc-200 rounded-2xl px-4 bg-white">
          <AccordionTrigger className="font-heading text-base font-semibold hover:no-underline" data-testid="section-legal">Pages légales (CGU, Confidentialité, Annulation)</AccordionTrigger>
          <AccordionContent className="space-y-4 pt-2">
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              <strong>Astuce :</strong> laissez vide pour utiliser le texte juridique par défaut (déjà conforme Loi 25 Québec / LPRPDE). Markdown supporté : <code>#</code> titre, <code>##</code> sous-titre, <code>**gras**</code>, <code>- liste</code>.
            </div>
            <TextField label="Résumé annulation (1 ligne, affiché sur la page de réservation)" value={form.cancellation_summary} onChange={setField("cancellation_summary")} placeholder="Annulation gratuite à plus de 7 jours, 50 % entre 7 j et 48 h, aucun remboursement à moins de 48 h." testId="setting-cancellation-summary" />
            <TextAreaField label="Conditions générales d'utilisation (CGU) — /cgu" value={form.cgu_content} onChange={setField("cgu_content")} placeholder="Laissez vide pour le texte par défaut..." testId="setting-cgu-content" rows={10} />
            <TextAreaField label="Politique de confidentialité — /confidentialite" value={form.privacy_content} onChange={setField("privacy_content")} placeholder="Laissez vide pour le texte par défaut..." testId="setting-privacy-content" rows={10} />
            <TextAreaField label="Politique d'annulation et remboursement — /annulation" value={form.cancellation_content} onChange={setField("cancellation_content")} placeholder="Laissez vide pour le texte par défaut..." testId="setting-cancellation-content" rows={10} />
          </AccordionContent>
        </AccordionItem>

        {/* === BOOKING DISCLAIMER === */}
        <AccordionItem value="booking-disclaimer" className="border border-zinc-200 rounded-2xl px-4 bg-white">
          <AccordionTrigger className="font-heading text-base font-semibold hover:no-underline" data-testid="section-booking-disclaimer">Conditions de réservation (encadré jaune avant le bouton "Envoyer la demande")</AccordionTrigger>
          <AccordionContent className="space-y-4 pt-2">
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              <strong>Astuce :</strong> laissez vide pour utiliser le texte par défaut. Le nom de votre site est inséré automatiquement où apparaissait "VroumZy".
            </div>
            <TextField label="Titre" value={form.booking_disclaimer_title} onChange={setField("booking_disclaimer_title")} placeholder="Conditions de réservation" testId="setting-disclaimer-title" />
            <TextAreaField
              label="Phrase d'introduction"
              value={form.booking_disclaimer_intro}
              onChange={setField("booking_disclaimer_intro")}
              placeholder={`En cliquant sur "Envoyer la demande", je reconnais et j'accepte que :`}
              testId="setting-disclaimer-intro"
              rows={2}
            />
            <TextAreaField
              label="Liste des conditions (une condition par ligne, sans tiret)"
              value={form.booking_disclaimer_bullets}
              onChange={setField("booking_disclaimer_bullets")}
              placeholder={`Je suis seul(e) responsable du respect des règles de l'hôte...
**VroumZy n'est pas responsable** des dommages, infractions...
Il est de mon devoir, ainsi que de celui de toute personne...
L'hôte peut annuler ma réservation sans remboursement si...`}
              testId="setting-disclaimer-bullets"
              rows={6}
            />
            <TextAreaField
              label="Texte de la case à cocher d'acceptation"
              value={form.booking_disclaimer_accept_label}
              onChange={setField("booking_disclaimer_accept_label")}
              placeholder="J'accepte ces conditions et je dégage VroumZy.com de toute responsabilité."
              testId="setting-disclaimer-accept"
              rows={2}
            />
          </AccordionContent>
        </AccordionItem>

        {/* === PROMO BANNER === */}
        <AccordionItem value="banner" className="border border-zinc-200 rounded-2xl px-4 bg-white">
          <AccordionTrigger className="font-heading text-base font-semibold hover:no-underline" data-testid="section-banner">Bandeau marketing (haut du site)</AccordionTrigger>
          <AccordionContent className="space-y-4 pt-2">
            <div className="flex items-center justify-between rounded-xl border border-zinc-200 p-3 bg-zinc-50">
              <div>
                <p className="text-sm font-semibold text-zinc-900">Afficher le bandeau</p>
                <p className="text-xs text-zinc-500">Activez pour faire apparaitre le bandeau en haut de toutes les pages.</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={!!form.banner_enabled}
                onClick={() => setField("banner_enabled")(!form.banner_enabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${form.banner_enabled ? "bg-emerald-600" : "bg-zinc-300"}`}
                data-testid="banner-enabled-toggle"
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${form.banner_enabled ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
            </div>

            <TextAreaField
              label="Message"
              value={form.banner_text}
              onChange={setField("banner_text")}
              placeholder="🎉 Frais d'inscription à 0 $ jusqu'au 12 mars !"
              testId="setting-banner-text"
              rows={2}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <TextField label="Libellé du lien (optionnel)" value={form.banner_link_label} onChange={setField("banner_link_label")} placeholder="En profiter" testId="setting-banner-link-label" />
              <TextField label="URL du lien (optionnel)" value={form.banner_link_url} onChange={setField("banner_link_url")} placeholder="/subscription ou https://..." testId="setting-banner-link-url" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Couleur de fond (HEX)</Label>
              <div className="flex gap-2 items-center">
                <input
                  type="color"
                  value={form.banner_bg_color || "#059669"}
                  onChange={(e) => setField("banner_bg_color")(e.target.value)}
                  className="h-10 w-12 rounded-lg border border-zinc-200 cursor-pointer"
                  data-testid="setting-banner-color-picker"
                />
                <Input
                  value={form.banner_bg_color || ""}
                  onChange={(e) => setField("banner_bg_color")(e.target.value)}
                  placeholder="#059669"
                  className="rounded-xl flex-1"
                  data-testid="setting-banner-color-input"
                />
              </div>
            </div>

            {form.banner_text && (
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Aperçu</Label>
                <div className="rounded-xl overflow-hidden" style={{ background: form.banner_bg_color || "#059669" }}>
                  <div className="px-4 py-2.5 text-center text-white text-sm">
                    <span className="font-medium">{form.banner_text}</span>
                    {form.banner_link_url && form.banner_link_label && (
                      <span className="underline font-semibold ml-2">{form.banner_link_label} →</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </AccordionContent>
        </AccordionItem>

        {/* === PRE-LAUNCH === */}
        <AccordionItem value="prelaunch" className="border border-amber-200 rounded-2xl px-4 bg-amber-50/40">
          <AccordionTrigger className="font-heading text-base font-semibold hover:no-underline" data-testid="section-prelaunch">
            <span className="flex items-center gap-2">
              Mode pré-lancement
              {form.prelaunch_enabled && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-200 text-amber-900 font-bold">ACTIF</span>}
            </span>
          </AccordionTrigger>
          <AccordionContent className="space-y-4 pt-2">
            <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 space-y-2">
              <p><strong>Comportement actuel</strong> : quand ce mode est activé,</p>
              <ul className="list-disc pl-5 space-y-1 text-xs">
                <li>✅ Tout le monde peut <strong>explorer le site</strong> et voir les terrains publiés.</li>
                <li>✅ Tout le monde peut <strong>créer un compte</strong> et <strong>publier ses terrains</strong>.</li>
                <li>🚫 Les <strong>réservations sont bloquées</strong> (côté UI et côté API). Un message « Réservations bientôt disponibles » s'affiche.</li>
                <li>📢 Une bannière jaune apparaît en haut de toutes les pages pour informer les visiteurs.</li>
                <li>👤 Toi (admin) gardes la possibilité de tester le flow complet de bout en bout.</li>
              </ul>
              <p className="text-xs">Désactivez ce mode pour ouvrir officiellement les réservations.</p>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                role="switch"
                aria-checked={!!form.prelaunch_enabled}
                onClick={() => setField("prelaunch_enabled")(!form.prelaunch_enabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${form.prelaunch_enabled ? "bg-amber-600" : "bg-zinc-300"}`}
                data-testid="prelaunch-enabled-toggle"
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${form.prelaunch_enabled ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
              <Label className="text-sm font-semibold">{form.prelaunch_enabled ? "Mode pré-lancement ACTIVÉ — réservations bloquées" : "Mode pré-lancement désactivé — réservations ouvertes"}</Label>
            </div>

            <p className="text-xs text-zinc-500 italic">Les champs ci-dessous étaient utilisés par l'ancienne page de splash (désormais supprimée). Ils sont conservés pour une éventuelle réutilisation future.</p>
            <TextField label="Titre principal (legacy)" value={form.prelaunch_title} onChange={setField("prelaunch_title")} placeholder="Lancement bientôt" testId="setting-prelaunch-title" />
            <TextField label="Sous-titre / message (legacy)" value={form.prelaunch_subtitle} onChange={setField("prelaunch_subtitle")} placeholder="Rejoignez la liste d'attente et soyez averti dès l'ouverture de VroumZy." testId="setting-prelaunch-subtitle" />
            <TextField label="Texte du bouton (legacy)" value={form.prelaunch_button_text} onChange={setField("prelaunch_button_text")} placeholder="Prévenez-moi du lancement" testId="setting-prelaunch-button" />
            <ImageField label="Image de fond (legacy)" value={form.prelaunch_image} onChange={setField("prelaunch_image")} uploading={uploading} setUploading={setUploading} testId="setting-prelaunch-image" ratio="h-40" />

            <a href="/admin?tab=waitlist" className="inline-flex items-center gap-1.5 text-sm text-amber-800 hover:text-amber-900 font-semibold underline">
              Voir la liste d'attente →
            </a>
          </AccordionContent>
        </AccordionItem>

        {/* === Navigation order (top menu) === */}
        <AccordionItem value="nav-order" className="border border-zinc-200 rounded-2xl px-4 bg-white">
          <AccordionTrigger className="font-heading text-base font-semibold hover:no-underline" data-testid="section-nav-order">Ordre du menu (bandeau du haut)</AccordionTrigger>
          <AccordionContent className="space-y-3 pt-2">
            <p className="text-xs text-zinc-500">Réorganisez les liens du menu de navigation. Utilisez les flèches pour monter ou descendre chaque item.</p>
            {(() => {
              const LABELS = {
                explore: "Explorer",
                create: "Publier un terrain",
                superstar: "Superstar",
                about: "À propos",
              };
              const move = (idx, dir) => {
                const next = [...navOrder];
                const j = idx + dir;
                if (j < 0 || j >= next.length) return;
                [next[idx], next[j]] = [next[j], next[idx]];
                setNavOrder(next);
              };
              return (
                <>
                  <ul className="space-y-2" data-testid="nav-order-list">
                    {navOrder.map((key, idx) => (
                      <li key={key} className="flex items-center justify-between gap-3 rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2" data-testid={`nav-order-item-${key}`}>
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold flex items-center justify-center">{idx + 1}</span>
                          <span className="text-sm font-medium text-zinc-900">{LABELS[key] || key}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 rounded-lg"
                            disabled={idx === 0}
                            onClick={() => move(idx, -1)}
                            data-testid={`nav-order-up-${key}`}
                            aria-label="Monter"
                          >
                            <ArrowUp className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 rounded-lg"
                            disabled={idx === navOrder.length - 1}
                            onClick={() => move(idx, 1)}
                            data-testid={`nav-order-down-${key}`}
                            aria-label="Descendre"
                          >
                            <ArrowDown className="h-4 w-4" />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-zinc-500 hover:text-zinc-700"
                      onClick={() => setNavOrder(["explore", "create", "superstar", "about"])}
                      data-testid="nav-order-reset"
                    >
                      <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Ordre par défaut
                    </Button>
                  </div>
                </>
              );
            })()}
          </AccordionContent>
        </AccordionItem>

        {/* === About page (/about) === */}
        <AccordionItem value="about" className="border border-zinc-200 rounded-2xl px-4 bg-white">
          <AccordionTrigger className="font-heading text-base font-semibold hover:no-underline" data-testid="section-about">Page À propos (/about)</AccordionTrigger>
          <AccordionContent className="space-y-4 pt-2">
            <p className="text-xs text-zinc-500">Personnalisez la page accessible depuis le menu &quot;À propos&quot;. Vous pouvez utiliser du markdown léger : <code># Titre</code>, <code>## Sous-titre</code>, <code>**gras**</code>, <code>- liste</code>.</p>
            <ImageField label="Image principale (hero)" value={form.about_image} onChange={setField("about_image")} uploading={uploading} setUploading={setUploading} testId="setting-about-image" ratio="h-48" />
            <TextField label="Titre principal" value={form.about_title} onChange={setField("about_title")} placeholder="À propos de VroumZy" testId="setting-about-title" />
            <TextField label="Sous-titre" value={form.about_subtitle} onChange={setField("about_subtitle")} placeholder="L'histoire derrière la plateforme." testId="setting-about-subtitle" />
            <TextAreaField label="Contenu (markdown supporté)" value={form.about_content} onChange={setField("about_content")} placeholder={"## Notre mission\nVroumZy reconnecte...\n\n## Pourquoi nous ?\n- Authentique\n- Sécuritaire\n- Local"} testId="setting-about-content" rows={14} />
          </AccordionContent>
        </AccordionItem>

        {/* === SEO === */}
        <AccordionItem value="seo" className="border border-zinc-200 rounded-2xl px-4 bg-white">
          <AccordionTrigger className="font-heading text-base font-semibold hover:no-underline" data-testid="section-seo">SEO (Google, partage sociaux)</AccordionTrigger>
          <AccordionContent className="space-y-4 pt-2">
            <TextField label="Titre de l'onglet navigateur" value={form.seo_title} onChange={setField("seo_title")} placeholder="ParkBnBe - Reservez votre terrain de camping" testId="setting-seo-title" />
            <TextAreaField label="Description pour Google" value={form.seo_description} onChange={setField("seo_description")} placeholder="Trouvez et reservez des terrains de camping privés partout au Québec..." testId="setting-seo-desc" rows={2} />
          </AccordionContent>
        </AccordionItem>

      </Accordion>

      {/* Sticky save bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-zinc-200 px-4 py-3 z-40">
        <div className="max-w-5xl mx-auto flex justify-end gap-2">
          <Button variant="outline" className="rounded-xl" onClick={() => window.location.reload()} data-testid="cancel-settings-btn">Annuler</Button>
          <Button onClick={handleSave} disabled={saving || uploading} className="bg-emerald-700 text-white hover:bg-emerald-800 rounded-xl px-6" data-testid="save-settings-btn">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Sauvegarde..." : "Sauvegarder les modifications"}
          </Button>
        </div>
      </div>

      <AdminPasswordDialog
        open={pwdOpen}
        onOpenChange={(v) => { if (!v) { setPwdOpen(false); setPwdError(""); } }}
        loading={saving}
        error={pwdError}
        title="Confirmer les modifications du site"
        description="Saisissez votre mot de passe administrateur pour appliquer les changements."
        confirmLabel="Sauvegarder"
        onConfirm={submitWithPassword}
      />
    </div>
  );
}
